import { patientViewIdAtom } from '@/features/patient-profile/atoms';
import { type PatientProfileSurface } from '@heidi/patients';
import {
  patientProfileOpened,
  patientProfileTabViewed,
} from '@heidi/patients/contributions';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { emitPatientSurfaceEvent } from './emit';
import { resolvePatientSurfaceFlagVariant } from './flag-variant';

type ViewTrackingArgs = {
  /** drawer or full_page — known at each root, so passed explicitly. */
  surface: PatientProfileSurface;
  /** Null until the view's patient is resolved; tracking waits for it. */
  patientId: string | null | undefined;
  openedFrom: 'list_row' | 'in_session_chip' | 'direct_link' | 'other';
  originatingSessionId?: string;
  isReturningPatient?: boolean;
};

/**
 * Owns the `view_id` lifecycle for a patient-profile view and fires
 * `patient_profile_opened` once per open. A fresh UUID is generated when the
 * view opens for a patient, written to {@link patientViewIdAtom} so every
 * in-view event (and a surface-started `session_created`) can stitch to it, and
 * cleared on close. Returns `trackTabViewed` for the tab strip.
 *
 * Snowplow-only: dispatches pass `options: { amplitude: false }`.
 */
export const usePatientProfileViewTracking = ({
  surface,
  patientId,
  openedFrom,
  originatingSessionId,
  isReturningPatient,
}: ViewTrackingArgs) => {
  const setViewId = useSetAtom(patientViewIdAtom);
  // The view_id of the patient we've already emitted `opened` for, so a
  // re-render (or switching patient within the same mounted view) emits exactly
  // one event per opened patient.
  const openedForPatientRef = useRef<string | null>(null);
  const viewIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!patientId) {
      // View closed (or no patient yet): reset so the next open re-emits.
      openedForPatientRef.current = null;
      viewIdRef.current = null;
      setViewId(null);
      return;
    }
    if (openedForPatientRef.current === patientId) return;

    const viewId = crypto.randomUUID();
    openedForPatientRef.current = patientId;
    viewIdRef.current = viewId;
    setViewId(viewId);

    emitPatientSurfaceEvent({
      event_name: patientProfileOpened.name,
      props: {
        view_id: viewId,
        patient_id: patientId,
        surface,
        opened_from: openedFrom,
        originating_session_id: originatingSessionId,
        is_returning_patient: isReturningPatient,
        feature_flag_variant: resolvePatientSurfaceFlagVariant(),
      },
    });
  }, [
    patientId,
    surface,
    openedFrom,
    originatingSessionId,
    isReturningPatient,
    setViewId,
  ]);

  // Clear the shared view_id when the view unmounts entirely.
  useEffect(
    () => () => {
      setViewId(null);
    },
    [setViewId]
  );

  const trackTabViewed = useCallback(
    (tab: 'sessions' | 'details' | 'files') => {
      if (!patientId) return;
      emitPatientSurfaceEvent({
        event_name: patientProfileTabViewed.name,
        props: {
          view_id: viewIdRef.current ?? '',
          patient_id: patientId,
          tab,
          surface,
          feature_flag_variant: resolvePatientSurfaceFlagVariant(),
        },
      });
    },
    [patientId, surface]
  );

  return { trackTabViewed };
};
